import { html, nothing } from "lit";
import type { ChannelsStatusSnapshot } from "../types.js";
import { formatAgo } from "../format.js";
import { renderLiveSessionsPanel, type LiveSessionsPanelProps } from "./live-sessions-panel.js";
import { renderRecordSkillPanel, type RecordSkillPanelProps } from "./record-skill-panel.js";

/**
 * Employee data from the gateway.
 */
export type EmployeeRow = {
  agentId: string;
  employee: {
    employeeId: string;
    name: string;
    /**
     * JAISIU-types-sweep: optional alternate display name (typically
     * the operator-set nickname or HR-imported friendly name). When
     * present, the employees view shows this in the row; otherwise it
     * falls back to `name`. Optional because directory fixtures
     * imported before the column shipped don't carry a value.
     */
    displayName?: string | null;
    email: string;
    department?: string;
    role?: string;
    status: "active" | "inactive" | "onboarding" | "terminated";
    startDate?: string;
    managerId?: string;
    costCenter?: string;
    phone?: string;
    location?: string;
    channels?: {
      allowed?: string[];
      permissions?: Record<
        string,
        {
          permission: "full" | "read-only" | "disabled";
          teams?: string[];
          allowedChats?: string[];
        }
      >;
    };
    metadata?: Record<string, unknown>;
    accounts?: Record<
      string,
      {
        profileId: string;
        label?: string;
        accountId?: string;
        username?: string;
        metadata?: Record<string, unknown>;
      }
    >;
  };
};

/**
 * HR connector info.
 */
export type ConnectorInfo = {
  id: string;
  name: string;
  version: string;
  description?: string;
  supportsBidirectional: boolean;
};

/**
 * Sync history entry.
 */
export type SyncHistoryRow = {
  id: string;
  timestamp: string;
  connector: string;
  result: "success" | "partial" | "failure";
  employeesAdded: number;
  employeesUpdated: number;
  employeesRemoved: number;
  errorsCount: number;
  durationMs: number;
};

/**
 * Employees view props.
 */
export type EmployeesProps = {
  loading: boolean;
  employees: EmployeeRow[];
  connectors: ConnectorInfo[];
  syncHistory: SyncHistoryRow[];
  channelsSnapshot: ChannelsStatusSnapshot | null;
  status: {
    enabled: boolean;
    connectorConfigured: boolean;
    /**
     * JAISIU-types-sweep: nullable. The gateway reports "no connector
     * selected" as JSON `null`, and both consumers already coalesce /
     * compare by identity (`status.activeConnector ?? "Connected"`,
     * `status?.activeConnector === c.id`), so `null` and `undefined`
     * render identically.
     */
    activeConnector?: string | null;
    employeeCount: number;
  } | null;
  accountOptions: {
    authProfiles: Array<{
      profileId: string;
      provider?: string;
      mode?: string;
      label?: string;
      available?: boolean;
    }>;
    channelAccounts: Array<{
      channelId: string;
      accountId: string;
      label?: string;
      isDefault?: boolean;
    }>;
  } | null;
  selectedEmployee: EmployeeRow | null;
  showCreateForm: boolean;
  error: string | null;
  onRefresh: () => void;
  onSelectEmployee: (employeeId: string | null) => void;
  onRunSync: (dryRun: boolean) => void;
  onTestConnection: () => void;
  onUpdateEmployee: (employeeId: string, data: Partial<EmployeeRow["employee"]>) => void;
  onAddChannel: (employeeId: string, channelId: string) => void;
  onRemoveChannel: (employeeId: string, channelId: string) => void;
  onCreateEmployee: (employee: Partial<EmployeeRow["employee"]>, agentId?: string) => void;
  onDeleteEmployee: (employeeId: string) => void;
  onToggleCreateForm: () => void;
  onNavigateDetail?: (employeeId: string) => void;
  liveSessions?: LiveSessionsPanelProps;
  recordSkill?: RecordSkillPanelProps;
};

/**
 * Get status badge color.
 */
function getStatusColor(status: "active" | "inactive" | "onboarding" | "terminated"): string {
  switch (status) {
    case "active":
      return "var(--ok, #22c55e)";
    case "onboarding":
      return "var(--warn, #f59e0b)";
    case "inactive":
      return "var(--muted, #6b7280)";
    case "terminated":
      return "var(--danger, #ef4444)";
  }
}

/**
 * Render employees list view.
 */
export function renderEmployees(props: EmployeesProps) {
  const { employees, loading, error } = props;

  // If a detail view is requested via routing (handled by app-render),
  // this component might still be called for the base /employees route.
  // The actual detail rendering is now handled in app-render.ts.

  return html`
    <section class="employees-view">
      <div class="row" style="justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
        <div>
          <h1 style="margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.02em;">Virtual Employees</h1>
          <p class="muted" style="margin: 4px 0 0 0; font-size: 14px;">
            Manage your AI workflow, employees, and HR system synchronization.
          </p>
        </div>
        <div class="row" style="gap: 12px;">
          <button
            class="btn"
            ?disabled=${loading}
            @click=${() => props.onRefresh()}
          >
            ${loading ? "Loading…" : "Refresh"}
          </button>
          <button
            class="btn primary"
            ?disabled=${loading}
            @click=${() => props.onToggleCreateForm()}
          >
            + Create Employee
          </button>
        </div>
      </div>

      ${
        error
          ? html`<div class="callout danger" style="margin-bottom: 20px;">
            ${error}
          </div>`
          : nothing
      }

      ${props.liveSessions ? renderLiveSessionsPanel(props.liveSessions) : nothing}
      ${props.recordSkill ? renderRecordSkillPanel(props.recordSkill) : nothing}

      ${props.showCreateForm ? renderCreateForm(props) : nothing}

      ${renderStatusCard(props)}

      <div class="employees-list-container" style="margin-top: 24px;">
        <div class="card" style="padding: 0; overflow: hidden;">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 600; font-size: 14px;">Employee Directory</span>
            <span class="muted" style="font-size: 12px;">${employees.length} total</span>
          </div>
          ${renderEmployeesList(props)}
        </div>
      </div>

      ${renderSyncSection(props)}
    </section>
  `;
}

/**
 * Render status card.
 */
function renderStatusCard(props: EmployeesProps) {
  const { status } = props;

  if (!status) {
    return nothing;
  }

  return html`
    <div class="stat-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 24px;">
      <div class="stat">
        <div class="stat-label">System Status</div>
        <div class="stat-value ${status.enabled ? "ok" : "warn"}" style="display: flex; align-items: center; gap: 8px;">
          <span class="statusDot ${status.enabled ? "ok" : ""}"></span>
          ${status.enabled ? "Active" : "Disabled"}
        </div>
      </div>
      <div class="stat">
        <div class="stat-label">Total Employees</div>
        <div class="stat-value">${status.employeeCount}</div>
      </div>
      <div class="stat">
        <div class="stat-label">HR Connector</div>
        <div class="stat-value" style="font-size: 18px; margin-top: 10px;">
          ${
            status.connectorConfigured
              ? html`<span class="chip-ok" style="padding: 4px 10px; border-radius: 6px; font-size: 13px;">${status.activeConnector ?? "Connected"}</span>`
              : html`
                  <span class="chip-warn" style="padding: 4px 10px; border-radius: 6px; font-size: 13px"
                    >Not Configured</span
                  >
                `
          }
        </div>
      </div>
    </div>
  `;
}

/**
 * Render employees list.
 */
function renderEmployeesList(props: EmployeesProps) {
  const { employees, selectedEmployee, onSelectEmployee } = props;

  if (employees.length === 0) {
    return html`
      <div style="padding: 40px; text-align: center;">
        <div class="muted" style="margin-bottom: 16px;">No employees found.</div>
        <button class="btn primary" @click=${() => props.onToggleCreateForm()}>
          Add Your First Employee
        </button>
      </div>
    `;
  }

  return html`
    <div style="overflow-x: auto;">
      <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 14px;">
        <thead>
          <tr style="background: var(--secondary); border-bottom: 1px solid var(--border);">
            <th style="padding: 12px 20px; font-weight: 600; color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;">Employee</th>
            <th style="padding: 12px 20px; font-weight: 600; color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;">Department / Role</th>
            <th style="padding: 12px 20px; font-weight: 600; color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;">Status</th>
            <th style="padding: 12px 20px; font-weight: 600; color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;">Access</th>
          </tr>
        </thead>
        <tbody>
          ${employees.map((row) => {
            const isSelected = selectedEmployee?.employee.employeeId === row.employee.employeeId;
            return html`
              <tr
                style="cursor: pointer; border-bottom: 1px solid var(--border); background: ${isSelected ? "var(--accent-subtle)" : "transparent"}; transition: background 0.2s;"
                @click=${() => {
                  onSelectEmployee(isSelected ? null : row.employee.employeeId);
                }}
                class="employee-row"
              >
                <td style="padding: 16px 20px;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="width: 36px; height: 36px; border-radius: 50%; background: var(--secondary); display: flex; align-items: center; justify-content: center; font-weight: 700; color: var(--accent); border: 1px solid var(--border);">
                      ${row.employee.name.charAt(0)}
                    </div>
                    <div>
                      <div style="font-weight: 600; color: var(--text-strong);">${row.employee.name}</div>
                      <div class="muted" style="font-size: 12px;">${row.employee.email}</div>
                    </div>
                  </div>
                </td>
                <td style="padding: 16px 20px;">
                  <div style="font-weight: 500;">${row.employee.department ?? "General"}</div>
                  <div class="muted" style="font-size: 12px;">${row.employee.role ?? "Staff"}</div>
                </td>
                <td style="padding: 16px 20px;">
                  <span
                    style="display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; background: ${getStatusColor(row.employee.status)}22; color: ${getStatusColor(row.employee.status)}; border: 1px solid ${getStatusColor(row.employee.status)}44;"
                  >
                    <span style="width: 6px; height: 6px; border-radius: 50%; background: currentColor;"></span>
                    ${row.employee.status.charAt(0).toUpperCase() + row.employee.status.slice(1)}
                  </span>
                </td>
                <td style="padding: 16px 20px;">
                  <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                    <div class="muted" style="font-size: 13px;">
                      <strong>${row.employee.channels?.allowed?.length ?? 0}</strong> Channels
                    </div>
                    <span class="muted" style="font-size: 12px; font-weight: 600;">
                      ${isSelected ? "Hide details ▲" : "Show details ▼"}
                    </span>
                  </div>
                </td>
              </tr>
              ${
                isSelected
                  ? html`
                      <tr>
                        <td colspan="4" style="padding: 0; border-bottom: 1px solid var(--border); background: var(--bg-elevated);">
                          <div style="padding: 16px 20px 20px;">
                            ${renderEmployeeDetail(props, row, { inline: true })}
                          </div>
                        </td>
                      </tr>
                    `
                  : nothing
              }
            `;
          })}
        </tbody>
      </table>
    </div>
    <style>
      .employee-row:hover {
        background: var(--bg-hover) !important;
      }
    </style>
  `;
}

/**
 * Render employee detail panel.
 */
function renderEmployeeDetail(
  props: EmployeesProps,
  employee: EmployeeRow,
  options: { inline?: boolean } = {},
) {
  const inline = options.inline ?? false;
  const { onUpdateEmployee, onAddChannel, onRemoveChannel, onSelectEmployee, channelsSnapshot } =
    props;
  const { employee: e } = employee;
  const accountEntries = Object.entries(e.accounts ?? {});
  const authProfiles = props.accountOptions?.authProfiles ?? [];
  const channelAccounts = props.accountOptions?.channelAccounts ?? [];
  const authProfileHints = authProfiles.map((profile) => profile.label ?? profile.profileId);
  const channelAccountHints = channelAccounts.map(
    (account) =>
      `${account.label ?? account.channelId}:${account.accountId}${account.isDefault ? " (default)" : ""}`,
  );
  const resolveProfileProvider = (profile: { profileId: string; provider?: string }) =>
    profile.provider ?? profile.profileId.split(":")[0] ?? profile.profileId;

  const providerOptions = Array.from(
    new Set([
      ...authProfiles.map((profile) => resolveProfileProvider(profile)),
      ...channelAccounts.map((account) => account.channelId),
    ]),
  ).filter(Boolean);
  const providerListId = `employee-${e.employeeId}-providers`;
  const accountListId = `employee-${e.employeeId}-channel-accounts`;
  const profileListId = `employee-${e.employeeId}-auth-profiles`;

  const resolveDefaultsForProvider = (provider: string) => {
    const matchedAccount =
      channelAccounts.find((account) => account.channelId === provider && account.isDefault) ??
      channelAccounts.find((account) => account.channelId === provider);
    const matchedProfile = authProfiles.find(
      (profile) => resolveProfileProvider(profile) === provider,
    );
    return {
      accountId: matchedAccount?.accountId,
      profileId: matchedProfile?.profileId ?? matchedAccount?.accountId,
      label: matchedProfile?.label ?? matchedAccount?.label,
    };
  };

  const applyAccountSelection = (
    container: HTMLElement | null,
    selection: {
      provider?: string;
      accountId?: string;
      profileId?: string;
      label?: string;
    },
  ) => {
    if (!container) {
      return;
    }
    const providerInput = container.querySelector<HTMLInputElement>("[data-account-provider]");
    const accountInput = container.querySelector<HTMLInputElement>("[data-account-id]");
    const profileInput = container.querySelector<HTMLInputElement>("[data-profile-id]");
    const labelInput = container.querySelector<HTMLInputElement>("[data-account-label]");

    if (providerInput && selection.provider) {
      providerInput.value = selection.provider;
    }
    if (accountInput) {
      accountInput.value = selection.accountId ?? "";
    }
    if (profileInput) {
      profileInput.value = selection.profileId ?? selection.accountId ?? "";
    }
    if (labelInput) {
      labelInput.value = selection.label ?? "";
    }
  };

  const matchesChannelAccount = (provider: string, accountId: string) =>
    channelAccounts.some(
      (account) => account.channelId === provider && account.accountId === accountId,
    );

  const validateAccountSelection = (params: {
    provider: string;
    accountId: string;
    profileId: string;
  }) => {
    const providerKnown = providerOptions.includes(params.provider);
    const profileKnown = authProfiles.some((profile) => profile.profileId === params.profileId);
    const accountKnown = params.accountId
      ? matchesChannelAccount(params.provider, params.accountId)
      : true;
    const profileMatchesChannel = matchesChannelAccount(params.provider, params.profileId);

    if (providerOptions.length > 0 && !providerKnown) {
      return confirm(`Provider "${params.provider}" is not in the known list. Continue anyway?`);
    }
    if (!profileKnown && !profileMatchesChannel) {
      return confirm(
        `Profile "${params.profileId}" is not in auth profiles or channel accounts. Continue anyway?`,
      );
    }
    if (!accountKnown) {
      return confirm(
        `Account "${params.accountId}" is not listed for ${params.provider}. Continue anyway?`,
      );
    }
    return true;
  };

  const saveAccountBinding = (params: {
    provider: string;
    accountId: string;
    profileId: string;
    label: string;
  }) => {
    const nextAccounts = { ...e.accounts };
    nextAccounts[params.provider] = {
      profileId: params.profileId,
      accountId: params.accountId || undefined,
      label: params.label || undefined,
    };
    onUpdateEmployee(e.employeeId, { accounts: nextAccounts });
  };

  return html`
    <div class="card" style="padding: 0; overflow: hidden; border-color: ${inline ? "var(--border)" : "var(--accent)"};">
      <div style="padding: 20px; background: var(--secondary); border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="width: 44px; height: 44px; border-radius: 50%; background: var(--accent); color: white; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700;">
            ${e.name.charAt(0)}
          </div>
          <div>
            <div style="font-weight: 700; font-size: 16px;">${e.name}</div>
            <div class="muted" style="font-size: 12px;">ID: ${e.employeeId}</div>
          </div>
        </div>
        <button
          class="btn-icon"
          style="padding: 8px; border-radius: ${inline ? "8px" : "50%"};"
          @click=${() => onSelectEmployee(null)}
        >
          ${inline ? "Collapse" : "✕"}
        </button>
      </div>

      <div style="padding: 20px;">
        <div style="display: grid; gap: 16px;">
          <div class="field">
            <span>Email Address</span>
            <input
              type="email"
              .value=${e.email}
              @change=${(ev: Event) =>
                onUpdateEmployee(e.employeeId, {
                  email: (ev.target as HTMLInputElement).value,
                })}
            />
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
            <div class="field">
              <span>Department</span>
              <input
                .value=${e.department ?? ""}
                @change=${(ev: Event) =>
                  onUpdateEmployee(e.employeeId, {
                    department: (ev.target as HTMLInputElement).value || undefined,
                  })}
              />
            </div>
            <div class="field">
              <span>Role</span>
              <input
                .value=${e.role ?? ""}
                @change=${(ev: Event) =>
                  onUpdateEmployee(e.employeeId, {
                    role: (ev.target as HTMLInputElement).value || undefined,
                  })}
              />
            </div>
          </div>

          <div class="field">
            <span>Employment Status</span>
            <select
              .value=${e.status}
                @change=${(ev: Event) =>
                  onUpdateEmployee(e.employeeId, {
                    status: (ev.target as HTMLSelectElement)
                      .value as EmployeeRow["employee"]["status"],
                  })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="onboarding">Onboarding</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>

          <div style="margin-top: 8px; padding-top: 16px; border-top: 1px solid var(--border);">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 12px;">Channel Permissions</div>
            
            <div style="margin-bottom: 16px;">
              <div style="font-size: 12px; color: var(--muted); margin-bottom: 8px;">Active Channels</div>
              <div class="chip-row" style="margin-bottom: 12px;">
                ${(e.channels?.allowed ?? []).map(
                  (channel) => html`
                    <span class="chip" style="display: flex; align-items: center; gap: 6px; padding: 4px 10px;">
                      ${channel}
                      <button
                        style="border: none; background: none; padding: 0; cursor: pointer; color: var(--muted); font-size: 14px;"
                        @click=${() => onRemoveChannel(e.employeeId, channel)}
                      >
                        ×
                      </button>
                    </span>
                  `,
                )}
                ${
                  (e.channels?.allowed ?? []).length === 0
                    ? html`
                        <div class="muted" style="font-size: 13px; font-style: italic">No channels assigned</div>
                      `
                    : nothing
                }
              </div>
            </div>

            ${
              channelsSnapshot
                ? html`
              <div style="margin-bottom: 16px;">
                <div style="font-size: 12px; color: var(--muted); margin-bottom: 8px;">Available in Instance</div>
                <div class="chip-row">
                  ${Object.keys(channelsSnapshot.channels)
                    .filter((id) => !(e.channels?.allowed ?? []).includes(id))
                    .map(
                      (id) => html`
                    <button class="chip" style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 4px 10px;" @click=${() => onAddChannel(e.employeeId, id)}>
                      + ${channelsSnapshot.channelLabels?.[id] ?? id}
                    </button>
                  `,
                    )}
                </div>
              </div>
            `
                : nothing
            }

            <div style="display: flex; gap: 8px;">
              <input
                type="text"
                placeholder="Add custom channel ID..."
                style="flex: 1; padding: 6px 12px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); font-size: 13px;"
                @keydown=${(ev: KeyboardEvent) => {
                  if (ev.key === "Enter") {
                    const input = ev.target as HTMLInputElement;
                    const channel = input.value.trim();
                    if (channel) {
                      onAddChannel(e.employeeId, channel);
                      input.value = "";
                    }
                  }
                }}
              />
              <button
                class="btn primary btn--sm"
                @click=${(ev: Event) => {
                  const input = (ev.target as HTMLButtonElement)
                    .previousElementSibling as HTMLInputElement;
                  const channel = input.value.trim();
                  if (channel) {
                    onAddChannel(e.employeeId, channel);
                    input.value = "";
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>

          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border);">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 12px;">Connected Accounts</div>
            <div style="margin-bottom: 12px; padding: 10px; border: 1px dashed var(--border); border-radius: 8px; background: var(--secondary);">
              <div style="font-weight: 600; font-size: 12px; margin-bottom: 6px;">Guided setup</div>
              <ol style="margin: 0; padding-left: 16px; font-size: 12px; color: var(--muted);">
                <li>Pick a provider to prefill defaults.</li>
                <li>Click a quick-pick chip to auto-save.</li>
                <li>Optionally edit fields and press Add Account.</li>
              </ol>
            </div>
            <div style="display: grid; gap: 8px; margin-bottom: 12px;">
              ${
                accountEntries.length > 0
                  ? accountEntries.map(
                      ([provider, account]) => html`
                    <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; border: 1px solid var(--border); border-radius: 8px;">
                      <div>
                        <div style="font-weight: 600; font-size: 13px;">${provider}</div>
                        <div class="muted" style="font-size: 12px;">
                          ${account.profileId}${account.accountId ? ` • acct:${account.accountId}` : ""}${account.label ? ` • ${account.label}` : ""}${account.username ? ` • ${account.username}` : ""}
                        </div>
                      </div>
                      <button
                        class="btn-icon"
                        style="padding: 6px;"
                        @click=${() => {
                          const nextAccounts = { ...e.accounts };
                          delete nextAccounts[provider];
                          onUpdateEmployee(e.employeeId, { accounts: nextAccounts });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  `,
                    )
                  : html`
                      <div class="muted" style="font-size: 13px; font-style: italic">No accounts connected</div>
                    `
              }
            </div>
            <div data-account-form style="display: grid; gap: 8px;">
              <input
                type="text"
                placeholder="Provider ID (e.g., github, whatsapp)"
                list=${providerListId}
                data-account-provider
                @change=${(ev: Event) => {
                  const input = ev.currentTarget as HTMLInputElement;
                  const provider = input.value.trim();
                  if (!provider) {
                    return;
                  }
                  const container = input.closest<HTMLElement>("[data-account-form]");
                  const defaults = resolveDefaultsForProvider(provider);
                  applyAccountSelection(container, { provider, ...defaults });
                }}
                style="padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); font-size: 13px;"
              />
              <datalist id=${providerListId}>
                ${providerOptions.map((provider) => html`<option value=${provider}></option>`)}
              </datalist>
              <input
                type="text"
                placeholder="Account ID (optional for channels)"
                list=${accountListId}
                data-account-id
                @change=${(ev: Event) => {
                  const input = ev.currentTarget as HTMLInputElement;
                  const container = input.closest<HTMLElement>("[data-account-form]");
                  const profileInput =
                    container?.querySelector<HTMLInputElement>("[data-profile-id]");
                  if (profileInput && !profileInput.value.trim()) {
                    profileInput.value = input.value.trim();
                  }
                }}
                style="padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); font-size: 13px;"
              />
              <datalist id=${accountListId}>
                ${channelAccounts.map(
                  (account) =>
                    html`<option value=${account.accountId} label=${`${account.label ?? account.channelId} (${account.channelId})`}></option>`,
                )}
              </datalist>
              <input
                type="text"
                placeholder="Profile ID / credential reference"
                list=${profileListId}
                data-profile-id
                style="padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); font-size: 13px;"
              />
              <datalist id=${profileListId}>
                ${authProfiles.map(
                  (profile) =>
                    html`<option value=${profile.profileId} label=${profile.label ?? profile.profileId}></option>`,
                )}
              </datalist>
              <input
                type="text"
                placeholder="Label or username (optional)"
                data-account-label
                style="padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); font-size: 13px;"
              />
              <button
                class="btn primary btn--sm"
                @click=${(ev: Event) => {
                  const container = (ev.currentTarget as HTMLButtonElement).closest(
                    "[data-account-form]",
                  );
                  const providerInput =
                    container?.querySelector<HTMLInputElement>("[data-account-provider]");
                  const accountInput =
                    container?.querySelector<HTMLInputElement>("[data-account-id]");
                  const profileInput =
                    container?.querySelector<HTMLInputElement>("[data-profile-id]");
                  const labelInput =
                    container?.querySelector<HTMLInputElement>("[data-account-label]");
                  const provider = providerInput?.value.trim() ?? "";
                  const accountId = accountInput?.value.trim() ?? "";
                  const profileId = profileInput?.value.trim() ?? accountId;
                  const label = labelInput?.value.trim() ?? "";
                  if (!provider || !profileId) {
                    return;
                  }
                  if (!validateAccountSelection({ provider, accountId, profileId })) {
                    return;
                  }
                  saveAccountBinding({ provider, accountId, profileId, label });
                  if (providerInput) {
                    providerInput.value = "";
                  }
                  if (accountInput) {
                    accountInput.value = "";
                  }
                  if (profileInput) {
                    profileInput.value = "";
                  }
                  if (labelInput) {
                    labelInput.value = "";
                  }
                }}
              >
                Add Account
              </button>
              ${
                authProfiles.length > 0
                  ? html`
                    <div>
                      <div class="muted" style="font-size: 12px; margin-bottom: 6px;">Quick pick auth profile</div>
                      <div class="chip-row">
                        ${authProfiles.map((profile) => {
                          const provider = resolveProfileProvider(profile);
                          return html`
                            <button
                              class="chip"
                              style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 4px 10px;"
                              @click=${(ev: Event) => {
                                const container = (
                                  ev.currentTarget as HTMLButtonElement
                                ).closest<HTMLElement>("[data-account-form]");
                                const selection = {
                                  provider,
                                  profileId: profile.profileId,
                                  label: profile.label ?? "",
                                  accountId: "",
                                };
                                applyAccountSelection(container, selection);
                                saveAccountBinding(selection);
                              }}
                            >
                              ${profile.label ?? profile.profileId}
                            </button>
                          `;
                        })}
                      </div>
                    </div>
                  `
                  : nothing
              }
              ${
                channelAccounts.length > 0
                  ? html`
                    <div>
                      <div class="muted" style="font-size: 12px; margin-bottom: 6px;">Quick pick channel account</div>
                      <div class="chip-row">
                        ${channelAccounts.map(
                          (account) => html`
                          <button
                            class="chip"
                            style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 4px 10px;"
                            @click=${(ev: Event) => {
                              const container = (
                                ev.currentTarget as HTMLButtonElement
                              ).closest<HTMLElement>("[data-account-form]");
                              const selection = {
                                provider: account.channelId,
                                accountId: account.accountId,
                                profileId: account.accountId,
                                label: account.label ?? "",
                              };
                              applyAccountSelection(container, selection);
                              saveAccountBinding(selection);
                            }}
                          >
                            ${account.label ?? account.channelId}:${account.accountId}${account.isDefault ? " (default)" : ""}
                          </button>
                        `,
                        )}
                      </div>
                    </div>
                  `
                  : nothing
              }
            </div>
            ${
              authProfileHints.length > 0
                ? html`<div class="muted" style="font-size: 12px; margin-top: 8px;">Auth profiles: ${authProfileHints.join(", ")}</div>`
                : nothing
            }
            ${
              channelAccountHints.length > 0
                ? html`<div class="muted" style="font-size: 12px; margin-top: 4px;">Channel accounts: ${channelAccountHints.join(", ")}</div>`
                : nothing
            }
          </div>

          <div style="margin-top: 12px; padding-top: 16px; border-top: 1px solid var(--border); display: flex; justify-content: space-between;">
            <button
              class="btn danger btn--sm"
              @click=${() => {
                if (confirm(`Are you sure you want to delete employee "${e.name}"?`)) {
                  props.onDeleteEmployee(e.employeeId);
                }
              }}
            >
              Delete Employee
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render HR sync section.
 */
function renderSyncSection(props: EmployeesProps) {
  const { connectors, syncHistory, status, onTestConnection, onRunSync } = props;

  return html`
    <div style="margin-top: 48px; padding-top: 32px; border-top: 1px solid var(--border);">
      <div style="margin-bottom: 24px;">
        <h2 style="margin: 0; font-size: 20px; font-weight: 700; letter-spacing: -0.01em;">HR Synchronization</h2>
        <p class="muted" style="margin: 4px 0 0 0; font-size: 14px;">
          Connect and sync employee data from your external HR systems.
        </p>
      </div>

      <div class="connectors-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; margin-bottom: 32px;">
        ${
          connectors.length > 0
            ? connectors.map(
                (c) => html`
                <div
                  class="card"
                  style="display: flex; flex-direction: column; justify-content: space-between; border-color: ${status?.activeConnector === c.id ? "var(--accent)" : "var(--border)"};"
                >
                  <div>
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
                      <div style="font-weight: 700; font-size: 15px;">${c.name}</div>
                      <span class="muted" style="font-size: 11px; font-family: var(--mono);">v${c.version}</span>
                    </div>
                    ${
                      c.description
                        ? html`<p class="muted" style="font-size: 13px; margin: 0 0 16px 0; line-height: 1.5;">${c.description}</p>`
                        : nothing
                    }
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="display: flex; gap: 6px;">
                      ${
                        c.supportsBidirectional
                          ? html`
                              <span
                                class="chip-ok"
                                style="font-size: 10px; padding: 2px 8px; text-transform: uppercase; font-weight: 700"
                                >Bidirectional</span
                              >
                            `
                          : nothing
                      }
                    </div>
                    ${
                      status?.activeConnector === c.id
                        ? html`
                            <span
                              class="chip-ok"
                              style="font-size: 10px; padding: 2px 8px; text-transform: uppercase; font-weight: 700"
                              >Active</span
                            >
                          `
                        : nothing
                    }
                  </div>
                </div>
              `,
              )
            : html`
                <div class="callout" style="grid-column: 1 / -1">No HR connectors available.</div>
              `
        }
      </div>

      ${
        status?.connectorConfigured
          ? html`
            <div class="card" style="margin-bottom: 32px; background: var(--secondary); border-style: dashed;">
              <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;">
                <div>
                  <div style="font-weight: 600; font-size: 14px;">Connector Actions</div>
                  <div class="muted" style="font-size: 13px;">Test your connection or perform a manual sync.</div>
                </div>
                <div style="display: flex; gap: 12px;">
                  <button
                    class="btn"
                    @click=${() => onTestConnection()}
                  >
                    Test Connection
                  </button>
                  <button
                    class="btn"
                    @click=${() => onRunSync(true)}
                  >
                    Dry Run
                  </button>
                  <button
                    class="btn primary"
                    @click=${() => onRunSync(false)}
                  >
                    Sync Now
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      ${
        syncHistory.length > 0
          ? html`
            <div>
              <div style="font-weight: 600; font-size: 14px; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
                Recent Sync History
                <span class="muted" style="font-weight: 400; font-size: 12px;">(Last 10 runs)</span>
              </div>
              <div class="card" style="padding: 0; overflow: hidden;">
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 13px;">
                  <thead>
                    <tr style="background: var(--secondary); border-bottom: 1px solid var(--border);">
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted);">Time</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted);">Connector</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted);">Result</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted); text-align: center;">Added</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted); text-align: center;">Updated</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted); text-align: center;">Removed</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted); text-align: center;">Errors</th>
                      <th style="padding: 12px 16px; font-weight: 600; color: var(--muted); text-align: right;">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${syncHistory.slice(0, 10).map(
                      (h) => html`
                        <tr style="border-bottom: 1px solid var(--border);">
                          <td style="padding: 12px 16px;">${formatAgo(new Date(h.timestamp).getTime())}</td>
                          <td style="padding: 12px 16px; font-weight: 500;">${h.connector}</td>
                          <td style="padding: 12px 16px;">
                            <span
                              class="chip-${h.result === "success" ? "ok" : h.result === "partial" ? "warn" : "danger"}"
                              style="padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase;"
                            >
                              ${h.result}
                            </span>
                          </td>
                          <td style="padding: 12px 16px; text-align: center;">${h.employeesAdded}</td>
                          <td style="padding: 12px 16px; text-align: center;">${h.employeesUpdated}</td>
                          <td style="padding: 12px 16px; text-align: center;">${h.employeesRemoved}</td>
                          <td style="padding: 12px 16px; text-align: center; color: ${h.errorsCount > 0 ? "var(--danger)" : "inherit"}; font-weight: ${h.errorsCount > 0 ? "600" : "400"};">${h.errorsCount}</td>
                          <td style="padding: 12px 16px; text-align: right;" class="muted">${h.durationMs}ms</td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          `
          : nothing
      }
    </div>
  `;
}

/**
 * Render create employee form.
 */
function renderCreateForm(props: EmployeesProps) {
  const [formData] = (function () {
    const key = "__create_employee_form_data";
    const saved = localStorage.getItem(key);
    let data = saved
      ? JSON.parse(saved)
      : {
          employeeId: "",
          name: "",
          email: "",
          status: "active",
          department: "",
          role: "",
          location: "",
          costCenter: "",
          phone: "",
          startDate: "",
          agentId: "",
        };

    return [
      data,
      (next: Record<string, unknown>) => {
        Object.assign(data, next);
        localStorage.setItem(key, JSON.stringify(data));
      },
    ];
  })();

  return html`
    <div
      class="card"
      style="background: var(--bg-elevated); margin-bottom: 32px; border-color: var(--accent); border-width: 2px; box-shadow: var(--shadow-lg);"
    >
      <div style="padding: 20px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center;">
        <div>
          <h2 style="margin: 0; font-size: 18px; font-weight: 700;">Create New Employee</h2>
          <p class="muted" style="margin: 4px 0 0 0; font-size: 13px;">Enter the details for the new virtual employee.</p>
        </div>
        <button class="btn-icon" style="padding: 8px;" @click=${() => props.onToggleCreateForm()}>
          ✕
        </button>
      </div>

      <div style="padding: 24px;">
        <div class="form-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px;">
          <label class="field">
            <span>Employee ID *</span>
            <input
              type="text"
              .value=${formData.employeeId}
              @input=${(ev: Event) => {
                formData.employeeId = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="e.g., EMP001"
            />
          </label>
          <label class="field">
            <span>Agent ID (optional)</span>
            <input
              type="text"
              .value=${formData.agentId}
              @input=${(ev: Event) => {
                formData.agentId = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="e.g., employee-emp001"
            />
          </label>
          <label class="field">
            <span>Full Name *</span>
            <input
              type="text"
              .value=${formData.name}
              @input=${(ev: Event) => {
                formData.name = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="e.g., John Doe"
            />
          </label>
          <label class="field">
            <span>Email Address *</span>
            <input
              type="email"
              .value=${formData.email}
              @input=${(ev: Event) => {
                formData.email = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="john.doe@company.com"
            />
          </label>
          <label class="field">
            <span>Department</span>
            <input
              type="text"
              .value=${formData.department}
              @input=${(ev: Event) => {
                formData.department = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="e.g., Engineering"
            />
          </label>
          <label class="field">
            <span>Role / Title</span>
            <input
              type="text"
              .value=${formData.role}
              @input=${(ev: Event) => {
                formData.role = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
              placeholder="e.g., Software Engineer"
            />
          </label>
          <label class="field">
            <span>Status</span>
            <select
              .value=${formData.status}
              @change=${(ev: Event) => {
                formData.status = (ev.target as HTMLSelectElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="onboarding">Onboarding</option>
              <option value="terminated">Terminated</option>
            </select>
          </label>
          <label class="field">
            <span>Start Date</span>
            <input
              type="date"
              .value=${formData.startDate}
              @input=${(ev: Event) => {
                formData.startDate = (ev.target as HTMLInputElement).value;
                localStorage.setItem("__create_employee_form_data", JSON.stringify(formData));
              }}
            />
          </label>
        </div>

        <div style="margin-top: 32px; display: flex; justify-content: flex-end; gap: 12px; padding-top: 24px; border-top: 1px solid var(--border);">
          <button
            class="btn"
            @click=${() => props.onToggleCreateForm()}
          >
            Cancel
          </button>
          <button
            class="btn primary"
            style="padding-left: 32px; padding-right: 32px;"
            @click=${() => {
              const latest = JSON.parse(
                localStorage.getItem("__create_employee_form_data") || "{}",
              );
              if (!latest.employeeId?.trim()) {
                alert("Employee ID is required");
                return;
              }
              if (!latest.name?.trim()) {
                alert("Name is required");
                return;
              }
              if (!latest.email?.trim()) {
                alert("Email is required");
                return;
              }

              const employee: Partial<EmployeeRow["employee"]> = {
                employeeId: latest.employeeId.trim(),
                name: latest.name.trim(),
                email: latest.email.trim(),
                status: latest.status ?? "active",
                department: latest.department?.trim() || undefined,
                role: latest.role?.trim() || undefined,
                location: latest.location?.trim() || undefined,
                costCenter: latest.costCenter?.trim() || undefined,
                phone: latest.phone?.trim() || undefined,
                startDate: latest.startDate?.trim() || undefined,
              };

              props.onCreateEmployee(employee, latest.agentId?.trim() || undefined);
              localStorage.removeItem("__create_employee_form_data");
              props.onToggleCreateForm();
            }}
          >
            Create Employee
          </button>
        </div>
      </div>
    </div>
  `;
}
